import { Display, setDisplay, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { H_100, POSITION, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement, findNearestMatch } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { anyReallyAny } from "@cervval.js/common/src/tools/any.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { NOW } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { DragAndDropService } from "./dragAndDrop.service";
import { CJS_DRAGGABLE_ITEM_ATTR, CJS_DROP_CONTAINER_ATTR, DragAndDropHorizontalDirection, DragAndDropPoint, DragAndDropVerticalDirection, DragRefConfig, PointerDirectionDelta } from "./dragAndDrop.types";
import { DragRefPreview } from "./dragRefPreview";

export const DRAG_PLACEHOLDER = "drag-placeholder";

/**
 * Reference to a draggable item. Used to manipulate or dispose of the item.
 */
export class DragRef<ItemData = anyReallyAny> {

    /**
     * Root DOM node of the drag instance. This is the element that will
     * be moved around as the user is dragging.
     */
    private _rootElement!: HTMLElement;

    /** 
     * Element that is rendered instead of the draggable item while it is being dragged
     * TODO Implement
     */
    private _placeholder?: HTMLElement;

    /** 
     * Container of placeholder (default is body)
     * TODO Implement
     */
    private _placeholderContainer?: HTMLElement;

    /** Coordinates on the page at which the user picked up the element. */
    private _pickupPositionOnPage!: DragAndDropPoint;

    /** Pointer position at which the last change in the delta occurred. */
    private _pointerPositionAtLastDirectionChange: DragAndDropPoint = { x: 0, y: 0 };

    /** true if d&d has started (currently dragging item(s)) */
    private _isDraggingStarted = false;

    /**
     * isDraggingStarted getter
     * true if d&d has started (currently dragging item(s))
     */
    public get isDraggingStarted(): boolean { return this._isDraggingStarted; }

    /** Current vertical direction of drag&drop, based on mouse Y */
    private _vDirection: DragAndDropVerticalDirection = null;

    /** Current horizontal direction of drag&drop, based on mouse X */
    private _hDirection: DragAndDropHorizontalDirection = null;

    /** Keeps track of the direction in which the user is dragging along each axis. */
    private _pointerDirectionDelta: PointerDirectionDelta = { x: 0, y: 0 };

    /** Event of drag&drop start */
    private _startEvent!: MouseEvent;

    /** 
     * Drag start time 
     * TODO implements drag start debounce
     */
    private _dragStartTime!: UnixTimestamp;

    /**
     * Anchor node used to save the place in the DOM where the element was
     * picked up so that it can be restored at the end of the drag sequence.
     * TODO Implements with placeholder
     */
    private _anchor!: Comment;

    /**
     * CSS `transform` applied to the element when it isn't being dragged. We need a
     * passive transform in order for the dragged element to retain its new position
     * after the user has stopped dragging and because we need to know the relative
     * position in case they start dragging again. This corresponds to `element.style.transform`.
     */
    private _passiveTransform: DragAndDropPoint = { x: 0, y: 0 };

    /** CSS `transform` that is applied to the element while it's being dragged. */
    private _activeTransform: DragAndDropPoint = { x: 0, y: 0 };

    /** Inline `transform` value that the element had before the first dragging sequence. */
    private _initialTransform?: string;

    /** @returns true if dragAndDrop is enable for this drag ref */
    public get disabled(): boolean {
        return Boolean(getVOFValue(this.config.disabled));
    }

    /** Element displayed next to the user's pointer while the element is dragged. */
    private _preview: DragRefPreview | null = null;


    constructor(
        public dragAndDropService: DragAndDropService,
        public element: HTMLElement,
        public data: ItemData,
        public config: DragRefConfig
    ) {
        this._initListeners();
    }


    /**
     * Clear position of a drag ref element
     */
    public clearDragRefElementPosition(): void {
        const styles = this.getDraggedElement().style;
        styles.transform = "";
    }

    /** Get element being dragged */
    public getDraggedElement(): HTMLElement {
        return this._placeholder ?? this._rootElement;
    }

    /** Dispose dragRef */
    public dispose(): void {
        this._removeDragSequenceListeners();

        this._destroyPreview();
        this._destroyPlaceholder();
        // TODO destroy dragRef and his listeners

    }

    /** Destroy dragRef */
    public destroy(): void {
        this.dispose();

        // Remove mousedown event
        this.element.removeEventListener("mousedown", this._mouseDownHandler);

        this.element.classList.remove("cjs-draggable-item");
        this.element.removeAttribute(CJS_DRAGGABLE_ITEM_ATTR);
    }

    /** Init dragRef listeners */
    private _initListeners(): void {

        // Add mouse down handler
        this.element.addEventListener("mousedown", this._mouseDownHandler);
    }

    /** Unsubscribes from the global subscriptions. */
    private _removeDragSequenceListeners(): void {

        // Remove mousemove event
        document.removeEventListener("mousemove", this._mouseMoveHandler);
        // Remove mouseup event
        document.removeEventListener("mouseup", this._mouseUpHandler);
    }

    /** Mouse down handler, called when user click on rootElement */
    private _mouseDownHandler: EventListenerObject = {
        handleEvent: this._initializeDragSequence.bind(this)
    };

    /** Mouse move handler */
    private _mouseMoveHandler: EventListenerObject = {
        handleEvent: (event: MouseEvent) => {

            if (this.disabled) { return; }
            const pointerPosition = this._getPointerPositionOnPage(event);
            this._updatePointerDirectionDelta(pointerPosition);

            // If drag hasn't start, start dragAndDrop sequence
            if (!this._isDraggingStarted) {
                this._isDraggingStarted = true;
                this._startDragSequence(event);
                return;
            } else {
                // Else element is moved
            }

            // If there's a position constraint function, we want the element's top/left to be at the
            // specific position on the page. Use the initial position as a reference if that's the case.
            const offset = this._pickupPositionOnPage;
            const activeTransform = this._activeTransform;
            if (this.isMoveAllowedX()) {
                activeTransform.x = pointerPosition.x - offset.x + this._passiveTransform.x;
            }
            if (this.isMoveAllowedY()) {
                activeTransform.y = pointerPosition.y - offset.y + this._passiveTransform.y;
            }

            // Apply transform to rootElement
            this._applyRootElementTransform(activeTransform.x, activeTransform.y);

            // Emit drag over event
            this.dragAndDropService.dragOverEvent.emit({
                uid: "dragover",
                data: this.data,
                event,
                hDirection: this._hDirection,
                vDirection: this._vDirection,
                source: this,
                startContainer: this._getDropContainerTarget(this._startEvent),// this._startContainer
                dropContainer: this._getDropContainerTarget(event),
                dropPoint: pointerPosition,
                distance: this._getDragDistance(pointerPosition)
            });
        }
    };

    /** Mouse up handler, define end a drag sequence if started */
    private _mouseUpHandler: EventListenerObject = {
        handleEvent: this._endDragSequence.bind(this)
    };

    /** Reset drag position of element */
    public resetDragPositionContext(pickupPosition?: DragAndDropPoint): void {
        this._pointerDirectionDelta = { x: 0, y: 0 };
        this._hDirection = null;
        this._vDirection = null;
        this._pointerPositionAtLastDirectionChange = { x: 0, y: 0 };
        this._initialTransform = undefined;
        this._activeTransform = { x: 0, y: 0 };
        this._passiveTransform = { x: 0, y: 0 };

        if (pickupPosition) {
            this._pickupPositionOnPage = pickupPosition;
        }
    }

    /** Get a new empty drag&drop context */
    private _resetContext(): void {
        this.resetDragPositionContext();
        this._isDraggingStarted = false;
    }

    /** Creates an element that will be shown instead of the current element while dragging. */
    private _createPlaceholderElement(): HTMLElement | undefined {
        // TODO implements
        if (!this.config.placeholder) { return undefined; }

        let placeholder = this.config.placeholder(this.data);
        if (placeholder === "global") {
            placeholder = this._rootElement.cloneNode(true) as HTMLElement;
        }

        // Stop pointer events on the preview so the user can't
        // interact with it while the preview is animating.
        placeholder.style.pointerEvents = "none";
        placeholder.classList.add(DRAG_PLACEHOLDER, POSITION.ABSOLUTE);

        // Assign attributes from rootElement
        placeholder.style.top = toPx(this._pickupPositionOnPage.y);
        placeholder.style.left = toPx(this._pickupPositionOnPage.x);
        placeholder.style.transform = this._rootElement.style.transform;

        // Create container of placeholder
        this._placeholderContainer = createHtmlElement("div", {
            cssClass: [W_100, H_100, POSITION.FIXED],
            style: {
                zIndex: String(this.config.zIndex ?? 1),
                top: "0",
                left: "0",
                pointerEvents: "none"
            },
            innerHTML: [placeholder]
        });

        // Append placeholder to body
        document.body.append(this._placeholderContainer);
        return placeholder;
    }

    /** Determines the point of the page that was touched by the user. */
    private _getPointerPositionOnPage(event: MouseEvent): DragAndDropPoint {
        const x = event.pageX;
        const y = event.pageY;
        // TODO Handle horizontal and vertical scroll
        return { x, y };
    }

    /** Get event target */
    private _getEventTarget(event: MouseEvent): HTMLElement {
        return (event.target ?? document.body) as HTMLElement;
    }

    /** 
     * Get drop container zone 
     * FIXME à revoir
     */
    private _getDropContainerTarget(event: MouseEvent): HTMLElement | undefined {
        const eltDragged = this.getDraggedElement();
        // Hide rootElement to get first element behind dragged element
        setDisplay(eltDragged, Display.NONE);

        // Get first element behind dragged element (where event was triggered)
        const pointerPosition = this._getPointerPositionOnPage(event);
        const elementBehind = document.elementFromPoint(pointerPosition.x, pointerPosition.y) as HTMLElement | null;
        if (!elementBehind) { return undefined; }

        const dropContainer = findNearestMatch(elementBehind, elt => elt.getAttribute(CJS_DROP_CONTAINER_ATTR) != null);

        // Restore rootElement visibility
        toggleVisible(eltDragged, true);

        return dropContainer;
    }

    /**
     * Sets up the different variables and subscriptions
     * that will be necessary for the dragging sequence.
     * @param event Browser event object that started the sequence.
     */
    private _initializeDragSequence(event: MouseEvent): void {
        this._resetContext();
        if (this.disabled) { return; }

        // Store last pointer position
        const pointerPosition = this._getPointerPositionOnPage(event);
        this._pointerPositionAtLastDirectionChange = { x: pointerPosition.x, y: pointerPosition.y };
        this._pickupPositionOnPage = { x: pointerPosition.x, y: pointerPosition.y };

        // Update drag context
        this._startEvent = event;

        // Assign root element
        this._rootElement = this.element;

        // TODO implements
        // this._placeholder = this._createPlaceholderElement();

        // Store startTime
        this._dragStartTime = NOW();

        // Store direction delta
        this._pointerDirectionDelta = { x: 0, y: 0 };

        // Attach the listeners to `document`
        document.addEventListener("mousemove", this._mouseMoveHandler);
        document.addEventListener("mouseup", this._mouseUpHandler);
    }

    /** Starts the dragging sequence. */
    private _startDragSequence(event: MouseEvent): void {

        const element = this._rootElement;
        const parent = element.parentNode as HTMLElement;
        const anchor = this._anchor = this._anchor || document.createComment("");// TODO to implements

        // Insert an anchor node so that we can restore the element's position in the DOM.
        parent.insertBefore(anchor, element);

        // There's no risk of transforms stacking when inside a drop container so
        // we can keep the initial transform up to date any time dragging starts.
        this._initialTransform = element.style.transform || "";

        // Create the preview after the initial transform has
        // been cached, because it can be affected by the transform.
        // TODO Handle preview
        // this._preview = new DragRefPreview(
        //     this._rootElement,
        //     this.config.previewClass ?? [],
        //     this._initialTransform,
        //     this.config.zIndex || 1000
        // );
        // this._preview.attach(document.body);

        const pointerPosition = this._getPointerPositionOnPage(event);

        // TODO garder startContainer pour ne pas aller le rechercher dans le move
        // TODO const startContainer = findFirstMatchingAncestor(this._startEvent.target as HTMLElement, elt => elt.getAttribute(CJS_DROP_CONTAINER_ATTR) != null);
        // Emit drag start event
        this.dragAndDropService.dragStartEvent.emit({
            uid: "dragstart",
            data: this.data,
            event,
            hDirection: this._hDirection,
            vDirection: this._vDirection,
            source: this,
            startContainer: this._getDropContainerTarget(this._startEvent),// this._startContainer
            dropContainer: this._getDropContainerTarget(event),
            dropPoint: pointerPosition,
            distance: this._getDragDistance(pointerPosition)
        });
    }


    /**
     * Clears subscriptions and stops the dragging sequence.
     * @param event Browser event object that ended the sequence.
     */
    private _endDragSequence(event: MouseEvent): void {
        this._removeDragSequenceListeners();

        // Note that here we use `isDragging` from the service, rather than from `this`.
        // The difference is that the one from the service reflects whether a dragging sequence
        // has been initiated, whereas the one on `this` includes whether the user has passed
        // the minimum dragging threshold.

        const pointerPosition = this._getPointerPositionOnPage(event);
        const dropPoint = this._getPointerPositionOnPage(event);
        const dropContainer = this._getDropContainerTarget(event);
        const startContainer = this._getDropContainerTarget(this._startEvent);
        const dragDistance = this._getDragDistance(pointerPosition);

        if (!this._isDraggingStarted || this.disabled) {

            // Emit abort drag event
            this.dragAndDropService.dragAbortEvent.emit({
                uid: "dragabort",
                data: this.data,
                event,
                source: this
            });

            // Always emit drag end event
            this.dragAndDropService.dragEndEvent.emit({
                uid: "dragend",
                data: this.data,
                event,
                hDirection: this._hDirection,
                vDirection: this._vDirection,
                source: this,
                distance: dragDistance,
                dropPoint,
                dropContainer,
                startContainer
            });
            return;
        }

        // Convert the active transform into a passive one. This means that next time
        // the user starts dragging the item, its position will be calculated relatively
        // to the new passive transform.
        this._passiveTransform.x = this._activeTransform.x;
        this._passiveTransform.y = this._activeTransform.y;

        this._updatePointerDirectionDelta(pointerPosition);

        // TODO Implements
        if (this._placeholder) {
            this._rootElement.style.transform = this._placeholder.style.transform;
            this._destroyPlaceholder();

        }

        if (dropContainer) {
            this.clearDragRefElementPosition();
            this.dragAndDropService.dropEvent.emit({
                uid: "drop",
                data: this.data,
                event,
                hDirection: this._hDirection,
                vDirection: this._vDirection,
                source: this,
                distance: dragDistance,
                dropPoint,
                dropContainer,
                startContainer
            });
        }

        this.dragAndDropService.dragEndEvent.emit({
            uid: "dragend",
            data: this.data,
            event,
            hDirection: this._hDirection,
            vDirection: this._vDirection,
            source: this,
            distance: dragDistance,
            dropPoint,
            dropContainer,
            startContainer
        });


        // Reset context
        this._resetContext();
    }


    /** Updates the current drag delta, based on the user's current pointer position on the page. */
    private _updatePointerDirectionDelta(pointerPosition: DragAndDropPoint): PointerDirectionDelta {
        const { x, y } = pointerPosition;
        const delta = this._pointerDirectionDelta;
        const positionSinceLastChange = this._pointerPositionAtLastDirectionChange;

        // Amount of pixels the user has dragged since the last time the direction changed.
        /* const changeX = Math.abs(x - positionSinceLastChange.x);
         const changeY = Math.abs(y - positionSinceLastChange.y);*/

        // Because we handle pointer events on a per-pixel basis, we don't want the delta
        // to change for every pixel, otherwise anything that depends on it can look erratic.
        // To make the delta more consistent, we track how much the user has moved since the last
        // delta change and we only update it after it has reached a certain threshold.

        delta.x = x > positionSinceLastChange.x ? 1 : -1;
        positionSinceLastChange.x = x;

        delta.y = y > positionSinceLastChange.y ? 1 : -1;
        positionSinceLastChange.y = y;

        this._vDirection = this._pointerDirectionDelta.y === 1 ? "down" : this._pointerDirectionDelta.y === 0 ? null : "up";
        this._hDirection = this._pointerDirectionDelta.x === 1 ? "right" : this._pointerDirectionDelta.y === 0 ? null : "left";

        return delta;
    }

    /**
     * Applies a `transform` to the root element, taking into account any existing transforms on it.
     * @param x New transform value along the X axis.
     * @param y New transform value along the Y axis.
     */
    private _applyRootElementTransform(x: number, y: number, options?: { clear?: boolean, rootElement?: boolean }): void {
        const element = options?.rootElement ? this._rootElement : this.getDraggedElement();

        const styles = element.style;
        if (options?.clear) {
            styles.transform = "";
            return;
        }

        const transform = this._getTransform(x, y);

        // Cache the previous transform amount only after the first drag sequence, because
        // we don't want our own transforms to stack on top of each other.
        // Should be excluded none because none + translate3d(x, y, x) is invalid css
        if (this._initialTransform == null) {
            this._initialTransform =
                styles.transform && styles.transform !== "none" ? styles.transform : "";
        }

        // Preserve the previous `transform` value, if there was one. Note that we apply our own
        // transform before the user's, because things like rotation can affect which direction
        // the element will be translated towards.
        styles.transform = this._combineTransforms(transform, this._initialTransform);
    }

    /**
     * Applies a `transform` to the preview, taking into account any existing transforms on it.
     * @param x New transform value along the X axis.
     * @param y New transform value along the Y axis.
     */
    private _applyPreviewTransform(x: number, y: number): void {
        // Apply the initial transform of the preview
        const initialTransform = this._initialTransform;
        const transform = this._getTransform(x, y);
        this._preview?.setTransform(this._combineTransforms(transform, initialTransform));
    }


    /**
     * Gets a 3d `transform` that can be applied to an element.
     * @param x Desired position of the element along the X axis.
     * @param y Desired position of the element along the Y axis.
     */
    private _getTransform(x: number, y: number): string {
        // Round the transforms since some browsers will
        // blur the elements for sub-pixel transforms.
        return `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0)`;
    }

    /**
     * Combines a transform string with an optional other transform
     * that exited before the base transform was applied.
     */
    private _combineTransforms(transform: string, initialTransform?: string): string {
        return initialTransform && initialTransform !== "none"
            ? transform + " " + initialTransform
            : transform;
    }

    /**
     * Gets the distance that the user has dragged during the current drag sequence.
     * @param currentPosition Current position of the user's pointer.
     */
    private _getDragDistance(currentPosition: DragAndDropPoint): DragAndDropPoint {
        const pickupPosition = this._pickupPositionOnPage;

        if (pickupPosition) {
            return { x: currentPosition.x - pickupPosition.x, y: currentPosition.y - pickupPosition.y };
        }

        return { x: 0, y: 0 };
    }

    /** Destroys the preview element and its ViewRef. */
    private _destroyPreview(): void {
        this._preview?.destroy();
        this._preview = null;
    }


    /** Destroys the placeholder element and its ViewRef. */
    private _destroyPlaceholder(): void {
        this._placeholder?.remove();
        this._placeholder = undefined;
        this._placeholderContainer?.remove();
        this._placeholderContainer = undefined;
    }

    /** @returns true if move is allowed on x direction */
    public isMoveAllowedX(): boolean {
        return !this.config.allowedDirections || this.config.allowedDirections === "x";
    }

    /** @returns true if move is allowed on y direction */
    public isMoveAllowedY(): boolean {
        return !this.config.allowedDirections || this.config.allowedDirections === "y";
    }

}